import React from 'react'
import ReactDOM from 'react-dom'
import { ThemeProvider } from 'styled-components'
import { ContentMenu } from 'components/contentMenu/contentMenu'
import { MobileHeaderLoginMenu } from 'components/header/headerLoginDropdown/mobileHeaderLoginMenu'
import { Menu } from 'components/menu/menu'
import { MobileUserMenu } from 'components/userDropdown/mobileUserMenu'
import { HeaderToggleContext } from 'helpers/contexts/headerToggleContext'
import { getTheme } from './ui/theme'
import { Hidden } from './ui/core'
import { Account } from 'types/account'
import { Header, MenuNode, ThemeVariant } from 'types'

type HeaderToggles = {
  onMobileSearchOpen: () => void
}

type HeaderRenderOptions = {
  theme: ThemeVariant
  toggles: HeaderToggles
}

type HeaderState = {
  theme: ThemeVariant
  userName: string
  isLoggedIn: boolean
  userMenuOpen: boolean
  accounts: Account[] | null
  accountsLoading: boolean
}

type HeaderMenuIslandApi = {
  renderMenu: (
    entry: Element,
    props: {
      nav: MenuNode[]
      open: boolean
      theme: ThemeVariant
      isLoggedIn: boolean
      userName: string
    },
    handlers: { toggleContentMenu: () => void; toggleUserMenu: () => void },
    options: HeaderRenderOptions,
  ) => void
  renderContentMenu: (
    entry: Element,
    props: Pick<Header, 'contentNav' | 'contentPromoImage' | 'theme'> & {
      open: boolean
    },
    handlers: { goBack: () => void },
    options: HeaderRenderOptions,
  ) => void
  renderMobileUserMenu: (
    entry: Element,
    props: Pick<
      HeaderState,
      'isLoggedIn' | 'userName' | 'accounts' | 'userMenuOpen' | 'accountsLoading'
    > & {
      theme: ThemeVariant
    },
    options: HeaderRenderOptions,
  ) => void
  unmount: (entry: Element) => void
}

declare global {
  interface Window {
    HusaHeaderReact?: HeaderMenuIslandApi
  }
}

const renderWithProviders = (
  entry: Element,
  children: React.ReactNode,
  { theme, toggles }: HeaderRenderOptions,
) => {
  ReactDOM.render(
    <React.StrictMode>
      <ThemeProvider theme={getTheme(theme)}>
        <HeaderToggleContext.Provider value={toggles}>
          {children}
        </HeaderToggleContext.Provider>
      </ThemeProvider>
    </React.StrictMode>,
    entry,
  )
}

window.HusaHeaderReact = {
  renderMenu: (entry, props, handlers, options) => {
    renderWithProviders(
      entry,
      <Menu
        nav={props.nav}
        open={props.open}
        theme={props.theme}
        toggleContentMenu={handlers.toggleContentMenu}
        toggleUserMenu={handlers.toggleUserMenu}
        isLoggedIn={props.isLoggedIn}
        userName={props.userName}
      />,
      options,
    )
  },
  renderContentMenu: (entry, props, handlers, options) => {
    renderWithProviders(
      entry,
      <ContentMenu
        theme={props.theme}
        nav={props.contentNav}
        open={props.open}
        promoImage={props.contentPromoImage}
        goBack={handlers.goBack}
      />,
      options,
    )
  },
  renderMobileUserMenu: (entry, props, options) => {
    renderWithProviders(
      entry,
      <Hidden above="md">
        {props.isLoggedIn ? (
          <MobileUserMenu
            userName={props.userName}
            theme={props.theme}
            accounts={props.accounts}
            open={props.userMenuOpen}
            accountsLoading={props.accountsLoading}
          />
        ) : (
          <MobileHeaderLoginMenu
            userName={props.userName}
            theme={props.theme}
            accounts={props.accounts}
            open={props.userMenuOpen}
            accountsLoading={props.accountsLoading}
          />
        )}
      </Hidden>,
      options,
    )
  },
  unmount: (entry) => ReactDOM.unmountComponentAtNode(entry),
}
